/**
 * Local-only performance telemetry. Samples the render loop and posts
 * JSON lines to /debug/perf-log.php (Valet), which appends them to
 * public/debug/perf.jsonl for offline analysis. Disabled everywhere
 * except *.test / localhost, so production never logs.
 */
export function createPerfLogger({ renderer, tier, getState }) {
  // Valet only: that's where the PHP sink exists. Static previews
  // (http-server) and production stay silent.
  const enabled = /\.test$/.test(location.hostname);
  if (!enabled) return { frame() {}, enabled: false };

  const session = Math.random().toString(36).slice(2, 8);
  const queue = [];
  let frames = 0;
  let accMs = 0;
  let worstMs = 0;
  let last = performance.now();
  let windowStart = last;

  // Accurate per-frame draw stats: we reset manually at frame start
  // (composer runs several internal renders per frame).
  renderer.info.autoReset = false;

  function frame() {
    const now = performance.now();
    const dt = now - last;
    last = now;
    if (dt > 1000) {
      // Tab was hidden; restart the sampling window.
      frames = 0;
      accMs = 0;
      worstMs = 0;
      windowStart = now;
      return;
    }
    frames += 1;
    accMs += dt;
    if (dt > worstMs) worstMs = dt;

    if (now - windowStart < 2000 || !frames) return;

    const info = renderer.info;
    const mem = performance.memory;
    queue.push({
      t: new Date().toISOString(),
      session,
      fps: +(1000 / (accMs / frames)).toFixed(1),
      worstMs: +worstMs.toFixed(1),
      calls: info.render.calls,
      tris: info.render.triangles,
      points: info.render.points,
      geos: info.memory.geometries,
      texs: info.memory.textures,
      progs: info.programs ? info.programs.length : null,
      heapMB: mem ? +(mem.usedJSHeapSize / 1048576).toFixed(1) : null,
      dpr: +renderer.getPixelRatio().toFixed(2),
      w: window.innerWidth,
      h: window.innerHeight,
      tier: tier.name,
      ...getState()
    });
    frames = 0;
    accMs = 0;
    worstMs = 0;
    windowStart = now;
    if (queue.length >= 3) flush();
  }

  function flush() {
    if (!queue.length) return;
    const body = JSON.stringify(queue.splice(0));
    fetch("/debug/perf-log.php", {
      method: "POST",
      body,
      keepalive: true
    }).catch(() => {});
  }

  window.addEventListener("pagehide", flush);

  return { frame, enabled: true };
}
